<script lang="ts">
  import { onMount } from 'svelte';
  import { link, push } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import { AppLoadingState, StatusBadge } from '../../framework/components/app';
  import { briefStatusTone } from './adminListBadges.js';
  import {
    agentSubjectKey,
    getAgentSubjectIcon,
    getAgentSubjectLabel,
  } from './adminListAgents.js';

  export let teamUuid: string;

  interface BriefRow {
    uuid: string;
    brief_type: string;
    brief_type_label: string;
    name: string;
    user?: { first_name: string; last_name: string; email: string } | null;
    status: string;
    progress: number;
    created_at: string;
    view_path: string;
  }

  let items: BriefRow[] = [];
  let isLoading = false;
  let currentPage = 1;
  let paging = { last_page: 1, total: 0, from: 0, to: 0 };

  onMount(() => loadBriefs());

  async function loadBriefs() {
    if (!teamUuid) return;
    isLoading = true;
    try {
      const response: any = await api.getAdminTeamBriefs(teamUuid, {
        page: currentPage,
        per_page: 10,
      });
      if (response.success) {
        items = response.data || [];
        paging = response.paging || paging;
      } else {
        items = [];
        toast.showNotification(false, response.message || 'Failed to load briefs');
      }
    } catch (error) {
      items = [];
      console.error(error);
    } finally {
      isLoading = false;
    }
  }

  function formatDate(value: string): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
  }

  function openBrief(row: BriefRow) {
    if (row.brief_type === 'built_to_brief') {
      push(`${row.view_path}?from=teams`);
      return;
    }
    push(row.view_path);
  }

  function goToPage(page: number) {
    currentPage = page;
    loadBriefs();
  }
</script>

<div class="admin-list admin-list--nested">
{#if isLoading}
  <AppLoadingState label="Loading agent work…" />
{:else if items.length === 0}
  <div class="empty-section">No agent work found for this team.</div>
{:else}
  <div class="table-wrap">
    <table class="data-table">
      <thead>
        <tr>
          <th><i class="cog icon" aria-hidden="true"></i> Agent</th>
          <th><i class="file outline icon" aria-hidden="true"></i> Name</th>
          <th><i class="user icon" aria-hidden="true"></i> User</th>
          <th><i class="signal icon" aria-hidden="true"></i> Status</th>
          <th><i class="calendar plus icon" aria-hidden="true"></i> Created</th>
        </tr>
      </thead>
      <tbody>
        {#each items as row (row.uuid)}
          {@const statusMeta = briefStatusTone(row.status)}
          <tr class="clickable data-row" on:click={() => openBrief(row)} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && openBrief(row)}>
            <td>
              <div class="type-wrapper">
                <div class="type-icon" data-subject={agentSubjectKey(row.brief_type)}>
                  <i class="{getAgentSubjectIcon(row.brief_type)} icon" aria-hidden="true"></i>
                </div>
                <span class="type-label">{getAgentSubjectLabel(row.brief_type)}</span>
              </div>
            </td>
            <td class="name-cell">{row.name}</td>
            <td class="user-cell">
              {#if row.user}
                {row.user.first_name} {row.user.last_name}
                <span class="email">{row.user.email}</span>
              {:else}
                —
              {/if}
            </td>
            <td>
              <StatusBadge tone={statusMeta.tone} label={row.status} />
            </td>
            <td class="date-cell">{formatDate(row.created_at)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  {#if paging.last_page > 1}
    <div class="pagination">
      <span class="pagination-info">Showing {paging.from || 0}–{paging.to || 0} of {paging.total}</span>
      <div class="pagination-buttons">
        <button type="button" class="pagination-btn" disabled={currentPage <= 1} on:click={() => goToPage(currentPage - 1)} aria-label="Previous page">Previous</button>
        <span class="page-indicator">Page {currentPage} of {paging.last_page}</span>
        <button type="button" class="pagination-btn" disabled={currentPage >= paging.last_page} on:click={() => goToPage(currentPage + 1)} aria-label="Next page">Next</button>
      </div>
    </div>
  {/if}
  {/if}
</div>

<style>
  .name-cell { font-weight: 500; max-width: 16rem; }
  .email { display: block; color: var(--grey-secondary); font-size: 0.8rem; }
</style>
